import React from 'react'
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined'
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined'
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined'
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined'

import {
  chatbotAgentsList,
  templatesList,
  apiKeysList,
  howToVideos,
  userGuide,
  support,
  userToolsList,
  skillsList,
  playground,
  teams,
  organizationSettings,
  organizationUsage,
  organizationAuditLog,
  memory,
} from '@constants/routing'
import { useAuth } from 'torqcloud-ui'
import useOrganizationSettingsAccess from '@/hooks/useOrganizationSettingsAccess'
import useOrganizationUsageAccess from '@/hooks/useOrganizationUsageAccess'
import useOrganizationAuditLogAccess from '@/hooks/useOrganizationAuditLogAccess'

import Sidebar, { SidebarItem, SidebarSection } from '../Layout/Sidebar/Sidebar'
import { appConfig } from '@/config'
import VideoPlayer from '@/icons/VideoPlayer'
import QuestionMarkCircled from '@/icons/QuestionMarkCircled'
import ChatMessage from '@/icons/ChatMessage'
import Chat from '@/icons/Chat'
import Globe from '@/icons/Globe'
import Template2 from '@/icons/Template2'
import Team from '@/icons/Team'
import KeyHorizontal from '@/icons/KeyHorizontal'

interface DashboardSidebarProps {
  onMobileClose: (isOpen?: boolean) => void
  openMobile: boolean
}

export default function DashboardSidebarTorqCloud({ onMobileClose, openMobile }: DashboardSidebarProps) {
  const { permissions } = useAuth()
  const { canAccessOrganizationSettings } = useOrganizationSettingsAccess()
  const { canViewOrganizationUsage } = useOrganizationUsageAccess()
  const { canViewOrganizationAuditLog } = useOrganizationAuditLogAccess()
  const iconStyle: React.CSSProperties = { width: '1.3rem', height: '1.3rem', margin: 0 }

  // Admins can view usage and the audit log without managing the organization, so they see Settings with only those.
  const settingsItems: SidebarItem[] = [
    ...(canAccessOrganizationSettings
      ? [
          {
            title: 'Organization Settings',
            path: organizationSettings(),
            icon: <BusinessOutlinedIcon style={iconStyle} />,
            childItems: [],
            // Usage lives under this path; keep this item from lighting up there too.
            exactMatch: true,
          },
        ]
      : []),
    ...(canViewOrganizationUsage
      ? [
          {
            title: 'Usage',
            path: organizationUsage(),
            icon: <InsertChartOutlinedIcon style={iconStyle} />,
            childItems: [],
          },
        ]
      : []),
    ...(canViewOrganizationAuditLog
      ? [
          {
            title: 'Audit Log',
            path: organizationAuditLog(),
            icon: <ManageSearchOutlinedIcon style={iconStyle} />,
            childItems: [],
          },
        ]
      : []),
  ]

  const sections: SidebarSection[] = [
    {
      title: 'Teammate Builder',
      items: [
        {
          title: 'Playground',
          path: playground(),
          icon: <Globe style={iconStyle} />,
          childItems: [],
        },
        {
          title: 'Teammates',
          path: chatbotAgentsList(),
          icon: <Chat style={iconStyle} />,
          childItems: [],
        },
        {
          title: 'Templates', // Agent Templates
          path: templatesList(),
          icon: <Template2 style={iconStyle} />,
          childItems: [],
        },
        ...(permissions?.canAccessMemories
          ? [
              {
                title: 'Memory',
                path: memory(),
                icon: <StorageOutlinedIcon style={iconStyle} />,
                childItems: [],
              },
            ]
          : []),
        ...(permissions?.canAccessTeamsV2
          ? [
              {
                title: 'Teams',
                path: teams(),
                icon: <Team style={iconStyle} />,
                childItems: [],
              },
            ]
          : []),
        ...(permissions?.canAccessUserTools
          ? [
              {
                title: 'Tools',
                path: userToolsList(),
                icon: <HandymanOutlinedIcon style={iconStyle} />,
                childItems: [],
              },
            ]
          : []),
        {
          title: 'Skills',
          path: skillsList(),
          icon: <AutoFixHighOutlinedIcon style={iconStyle} />,
          childItems: [],
        },
        {
          title: 'Keys & Secrets',
          path: apiKeysList(),
          icon: <KeyHorizontal style={iconStyle} />,
          childItems: [],
        },
      ],
    },
    ...(settingsItems.length > 0 ? [{ title: 'Settings', items: settingsItems }] : []),
    {
      title: 'Help & Support',
      items: [
        ...(appConfig.helpArticles.howToVideos
          ? [
              {
                title: 'How To Videos',
                path: howToVideos(),
                icon: <VideoPlayer style={iconStyle} />,
                childItems: [],
              },
            ]
          : []),
        ...(appConfig.userGuideUrl
          ? [
              {
                title: 'User Guide',
                path: userGuide(),
                icon: <QuestionMarkCircled style={iconStyle} />,
                childItems: [],
              },
            ]
          : []),
        {
          title: 'Support',
          path: support(),
          icon: <ChatMessage style={iconStyle} />,
          childItems: [],
        },
      ],
    },
  ]

  return <Sidebar sections={sections} onMobileClose={onMobileClose} openMobile={openMobile} />
}
